import { Link, useParams } from 'react-router-dom';
import { POSTS, type BlogPost } from '../content/blog.generated';
import { useMeta } from '../shared/useMeta';
import { SITE_NAME } from '../shared/site';
import ErrorScreen from '../shared/ui/ErrorScreen';
import '../content/seo.css';
import './blog.css';

export function formatPostDate(date: string): string {
  const [y, m, d] = date.split('-').map((n) => parseInt(n, 10));
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Shared chrome for the reading pages, so they aren't orphaned from the site. */
export function ProseShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="prose-page">
      <header className="prose-header">
        <Link to="/" className="prose-home">
          ← {SITE_NAME}
        </Link>
      </header>
      {children}
    </div>
  );
}

export function BlogIndexPage() {
  useMeta({
    title: 'Puzzle Strategy Guides',
    description:
      'Guides to solving cryptograms, word ladders, grouping puzzles and daily word games — how the puzzles work and how to get better at them.',
    path: '/blog',
  });

  return (
    <ProseShell>
      <article className="prose">
        <nav className="prose-crumbs" aria-label="Breadcrumb">
          <a href="/">All puzzles</a>
          <span aria-hidden> › </span>
          <span>Guides</span>
        </nav>
        <h1>Puzzle strategy guides</h1>
        <p>
          How the puzzles work, and how to be better at them — written by the people who build
          them. Every guide is free to read, like every puzzle on the site.
        </p>
        <ul className="prose-list">
          {POSTS.map((post) => (
            <li key={post.slug}>
              <Link to={`/blog/${post.slug}`}>{post.title}</Link>
              <p>{post.description}</p>
              <p className="prose-meta">
                {formatPostDate(post.date)} · {post.readingMinutes} min read
              </p>
            </li>
          ))}
        </ul>
      </article>
    </ProseShell>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  return post ? <Post post={post} /> : <ErrorScreen message="That guide doesn't exist." />;
}

function Post({ post }: { post: BlogPost }) {
  useMeta({ title: post.title, description: post.description, path: `/blog/${post.slug}` });
  return (
    <ProseShell>
      <article className="prose">
        <nav className="prose-crumbs" aria-label="Breadcrumb">
          <a href="/">All puzzles</a>
          <span aria-hidden> › </span>
          <a href="/blog">Guides</a>
        </nav>
        <h1>{post.title}</h1>
        <p className="prose-meta">
          {formatPostDate(post.date)} · {post.readingMinutes} min read
        </p>
        {/* Compiled from our own markdown at build time — no user input involved. */}
        <div dangerouslySetInnerHTML={{ __html: post.html }} />
        <p className="prose-back">
          <Link to="/blog">← All guides</Link>
        </p>
      </article>
    </ProseShell>
  );
}
